/**
 * Meta (Facebook) Pixel funnel fan-out.
 *
 * The pixel itself is initialized once in App.tsx via `useMetaPixel` (PageView).
 * This module maps our internal funnel events (fired through `track()`) to Meta
 * Standard Events, so the pixel reports Lead / CompleteRegistration in addition to
 * PageView — WITHOUT sprinkling `fbq()` calls across the 8+ signup sites.
 *
 * Design decisions:
 * - ONE standard event per funnel step (Lead, CompleteRegistration) regardless of
 *   surface. Meta optimizes best on a single healthy conversion stream; the surface
 *   (`web` | `tms` | `mobile` | `extension` | `loadhub`) rides along as
 *   `content_category` so Ads Manager can still break down by surface.
 * - Every event carries a deterministic `event_id` so a future server-side CAPI can
 *   deduplicate the same action (browser pixel + server) instead of double-counting.
 */
import { FunnelEvent, Surface, normalizeMethod } from './funnel'

// `window.fbq` is declared globally by useMetaPixel.ts (the pixel initializer). We only
// call it here; the extra `params`/`opts` args are part of Meta's runtime signature.

/** Internal funnel event -> Meta Standard Event. Only mapped events fire to Meta. */
const META_STANDARD_EVENT: Partial<Record<FunnelEvent, string>> = {
    [FunnelEvent.SIGNUP_STARTED]: 'Lead',
    [FunnelEvent.SIGNUP_COMPLETED]: 'CompleteRegistration',
}

/**
 * Builds an event id for pixel<->CAPI dedup. A fresh random id per fire — NEVER derived
 * from PII (email/userId). Dedup works because a future server-side CAPI for the SAME
 * action will send the SAME id (generated once and threaded through the signup payload),
 * not because the id is reconstructed from the user's identity. Using raw email here would
 * leak unhashed PII to Meta in the outgoing request.
 */
const buildMetaEventId = (): string =>
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`

/**
 * Mirrors a funnel event to the Meta Pixel as a Standard Event, if it maps to one.
 * No-ops when the pixel isn't loaded or the event isn't a funnel conversion.
 * NOTE: never receives PII — only non-identifying funnel metadata.
 */
export const emitMetaFunnelEvent = (event: FunnelEvent, surface: Surface, props?: { method?: string; value?: number; currency?: string }): void => {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return

    const standardEvent = META_STANDARD_EVENT[event]
    if (!standardEvent) return

    const params: Record<string, unknown> = {
        content_category: surface,
        registration_method: normalizeMethod(props?.method),
    }
    if (typeof props?.value === 'number') params.value = props.value
    if (props?.currency) params.currency = props.currency

    try {
        // Meta's fbq accepts (method, event, params, opts); the global type only models
        // the init/track basics, so widen it locally for the params + eventID overload.
        const fbq = window.fbq as (method: string, event: string, params?: Record<string, unknown>, opts?: { eventID?: string }) => void
        fbq('track', standardEvent, params, { eventID: buildMetaEventId() })
    } catch {
        /* Meta must never break the app */
    }
}
